/*
  Glimmery header behaviour. Loaded (defer) by sections/header.liquid.
  - <sticky-header>: Dawn's "on scroll up" sticky header.
  - <g-header-drawer>: full-height menu drawer built on Dawn's MenuDrawer (global.js). Slides in from
    the left (240 ms, --g-ease-out, CSS in g-header.css); the [open] attribute is removed after the
    slide-out has finished. Used below 990px (and on wide screens with the desktop menu type "drawer").
    Modal: outside content is inert, body scroll is locked, Escape / close button / backdrop close it,
    focus returns to the menu button. Every close path (incl. breakpoint change, bfcache restore and
    theme editor section reload) releases the scroll lock and the inert state.
*/
(() => {
  const isEscape = (event) => event.key === 'Escape' || event.key === 'Esc' || event.code === 'Escape';
  const isVisible = (element) => !!element && element.getClientRects().length > 0;

  /* ---------- Sticky header (Dawn 16) ---------- */
  if (!customElements.get('sticky-header')) {
    class StickyHeader extends HTMLElement {
      constructor() {
        super();
        this.setHeaderHeight = this.setHeaderHeight.bind(this);
      }

      connectedCallback() {
        this.header = this.closest('.section-header') || document.querySelector('.section-header');
        if (!this.header) return;
        this.mobileMediaQuery = window.matchMedia('(max-width: 990px)');
        this.headerIsAlwaysSticky = this.getAttribute('data-sticky-type') === 'always';
        this.headerBounds = {};

        this.setHeaderHeight();
        this.mobileMediaQuery.addEventListener('change', this.setHeaderHeight);

        if (this.headerIsAlwaysSticky) {
          this.header.classList.add('shopify-section-header-sticky');
        }

        this.currentScrollTop = 0;
        this.preventReveal = false;
        this.predictiveSearches = this.querySelectorAll('predictive-search');

        this.onScrollHandler = this.onScroll.bind(this);
        this.hideHeaderOnScrollUp = () => (this.preventReveal = true);

        this.addEventListener('preventHeaderReveal', this.hideHeaderOnScrollUp);
        window.addEventListener('scroll', this.onScrollHandler, { passive: true });

        this.createObserver();
      }

      disconnectedCallback() {
        if (!this.header) return;
        this.mobileMediaQuery.removeEventListener('change', this.setHeaderHeight);
        this.removeEventListener('preventHeaderReveal', this.hideHeaderOnScrollUp);
        window.removeEventListener('scroll', this.onScrollHandler);
      }

      setHeaderHeight() {
        document.documentElement.style.setProperty('--header-height', `${this.header.offsetHeight}px`);
      }

      createObserver() {
        const observer = new IntersectionObserver((entries) => {
          this.headerBounds = entries[0].intersectionRect;
          observer.disconnect();
        });
        observer.observe(this.header);
      }

      onScroll() {
        const scrollTop = window.pageYOffset || document.documentElement.scrollTop;

        if ([...this.predictiveSearches].some((predictiveSearch) => predictiveSearch.isOpen)) return;
        if (this.header.classList.contains('g-menu-open')) return;

        if (scrollTop > this.currentScrollTop && scrollTop > this.headerBounds.bottom) {
          this.header.classList.add('scrolled-past-header');
          if (this.preventHide) return;
          requestAnimationFrame(this.hide.bind(this));
        } else if (scrollTop < this.currentScrollTop && scrollTop > this.headerBounds.bottom) {
          this.header.classList.add('scrolled-past-header');
          if (!this.preventReveal) {
            requestAnimationFrame(this.reveal.bind(this));
          } else {
            window.clearTimeout(this.isScrolling);
            this.isScrolling = setTimeout(() => {
              this.preventReveal = false;
            }, 66);
            requestAnimationFrame(this.hide.bind(this));
          }
        } else if (scrollTop <= this.headerBounds.top) {
          this.header.classList.remove('scrolled-past-header');
          requestAnimationFrame(this.reset.bind(this));
        }

        this.currentScrollTop = scrollTop;
      }

      hide() {
        if (this.headerIsAlwaysSticky) return;
        this.header.classList.add('shopify-section-header-hidden', 'shopify-section-header-sticky');
        this.closeMenuDisclosure();
        this.closeSearchModal();
      }

      reveal() {
        if (this.headerIsAlwaysSticky) return;
        this.header.classList.add('shopify-section-header-sticky', 'animate');
        this.header.classList.remove('shopify-section-header-hidden');
      }

      reset() {
        if (this.headerIsAlwaysSticky) return;
        this.header.classList.remove('shopify-section-header-hidden', 'shopify-section-header-sticky', 'animate');
      }

      closeMenuDisclosure() {
        this.header.querySelectorAll('header-menu').forEach((disclosure) => disclosure.close());
      }

      closeSearchModal() {
        // Only close a search modal that is actually open: close() also releases the body scroll lock and focus
        // trap, which would otherwise be torn away from an unrelated open dialog (quick add, size guide).
        this.header.querySelectorAll('details-modal').forEach((searchModal) => {
          if (typeof searchModal.isOpen !== 'function' || searchModal.isOpen()) searchModal.close(false);
        });
      }
    }

    customElements.define('sticky-header', StickyHeader);
  }

  /* ---------- Account dialog anchor (Dawn 16), registered once ---------- */
  if (!window.glimmeryHeaderListeners) {
    window.glimmeryHeaderListeners = true;
    // The account `open` event is composed but does not bubble, so listen during capture.
    document.addEventListener(
      'open',
      (event) => {
        if (!(event.target instanceof HTMLElement) || !event.target.matches('shopify-account')) return;
        const bottom = Math.max(0, Math.round(event.target.getBoundingClientRect().bottom));
        event.target.style.setProperty('--account-dialog-top', `${bottom}px`);
      },
      true
    );

    // Escape pressed on the search's clear/submit button: predictive-search.js closes the results and
    // then focuses the input, and that focus reopens them (3 Escapes to leave). Moving focus to the
    // input first (capture phase, before the component's handler) makes its focus() a no-op, so one
    // Escape closes the results and the next one closes the search modal.
    document.addEventListener(
      'keydown',
      (event) => {
        if (!isEscape(event) || !(event.target instanceof Element)) return;
        const search = event.target.closest('predictive-search');
        if (!search || !search.hasAttribute('open')) return;
        const input = search.querySelector('input[type="search"]');
        if (!input || event.target === input) return;
        input.focus({ preventScroll: true });
      },
      true
    );
  }

  /* ---------- Menu drawer ---------- */
  const defineDrawer = () => {
    if (customElements.get('g-header-drawer')) return;
    const MenuDrawer = customElements.get('menu-drawer');
    if (!MenuDrawer) return;

    class GHeaderDrawer extends MenuDrawer {
      constructor() {
        super();
        this.summary = this.mainDetailsToggle.querySelector('summary');
        this.panel = this.querySelector('.g-drawer');
        this.inerted = [];
        this.isLocked = false;
        // The inline navigation takes over from 990px (assets/g-header.css); an open drawer closes there.
        this.desktopQuery = window.matchMedia('(min-width: 990px)');

        this.onBreakpointChange = this.onBreakpointChange.bind(this);
        this.onPageShow = this.onPageShow.bind(this);
        this.onPanelKeyUp = this.onPanelKeyUp.bind(this);

        // Capture phase: handled before Dawn's generic details/Escape listeners inside the panel.
        this.panel.addEventListener('keyup', this.onPanelKeyUp, true);
        this.querySelector('[data-g-menu-backdrop]')?.addEventListener('click', (event) =>
          this.closeMenuDrawer(event, this.summary)
        );
        // Safety net: whatever removes [open], the locks go with it.
        this.mainDetailsToggle.addEventListener('toggle', () => {
          if (!this.mainDetailsToggle.hasAttribute('open')) {
            this.releaseLocks();
            this.summary.setAttribute('aria-expanded', 'false');
          }
        });
      }

      connectedCallback() {
        this.summary.setAttribute('role', 'button');
        this.summary.setAttribute('aria-controls', this.panel.id);
        this.summary.setAttribute('aria-expanded', String(this.mainDetailsToggle.hasAttribute('open')));
        window.addEventListener('pageshow', this.onPageShow);
      }

      disconnectedCallback() {
        window.removeEventListener('pageshow', this.onPageShow);
        this.releaseLocks();
      }

      openMenuDrawer(summaryElement) {
        if (summaryElement) this.summary = summaryElement;
        this.releaseLocks();

        this.sectionHeader = this.closest('.section-header');
        this.sectionHeader?.classList.add('menu-open', 'g-menu-open');
        this.summary.setAttribute('aria-expanded', 'true');
        document.body.classList.add('g-scroll-lock');
        this.isLocked = true;
        this.desktopQuery.addEventListener('change', this.onBreakpointChange);

        // The click handler runs before <details> toggles, so wait a tick before animating and focusing.
        setTimeout(() => {
          if (!this.mainDetailsToggle.hasAttribute('open') || !this.isLocked) return;
          this.setOutsideInert();
          this.mainDetailsToggle.classList.add('menu-opening');
          const closeButton = this.panel.querySelector('[data-g-menu-close]');
          (closeButton || this.panel).focus({ preventScroll: true });
        });
      }

      closeMenuDrawer(event, elementToFocus) {
        // Dawn calls this without an event on focusout; the modal drawer stays open then.
        if (event === undefined) return;

        const details = this.mainDetailsToggle;
        details.classList.remove('menu-opening');
        details.querySelectorAll('details').forEach((nested) => {
          nested.removeAttribute('open');
          nested.classList.remove('menu-opening');
        });
        details.querySelectorAll('.submenu-open').forEach((submenu) => submenu.classList.remove('submenu-open'));

        this.releaseLocks();
        this.summary.setAttribute('aria-expanded', 'false');

        if (elementToFocus !== null) {
          const target = isVisible(elementToFocus || this.summary)
            ? elementToFocus || this.summary
            : this.closest('.g-header')?.querySelector('.g-nav a, .g-nav summary, .g-header__logo-link');
          target?.focus({ preventScroll: true });
        }

        this.closeAnimation(details);
      }

      onCloseButtonClick(event) {
        const button = event.currentTarget;
        if (button.hasAttribute('data-g-menu-close')) {
          event.preventDefault();
          this.closeMenuDrawer(event, this.summary);
        } else if (button.classList.contains('menu-drawer__close-button')) {
          super.onCloseButtonClick(event);
        }
        // Other buttons in the drawer (e.g. the localization submit) keep their native behaviour.
      }

      onPanelKeyUp(event) {
        if (!isEscape(event) || !this.mainDetailsToggle.hasAttribute('open')) return;
        event.stopPropagation();

        const openSubmenus = this.panel.querySelectorAll('details[open]');
        if (openSubmenus.length) {
          this.closeSubmenu(openSubmenus[openSubmenus.length - 1]);
          return;
        }
        this.closeMenuDrawer(event, this.summary);
      }

      closeAnimation(detailsElement) {
        const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        window.clearTimeout(detailsElement.gCloseTimer);
        detailsElement.gCloseTimer = window.setTimeout(
          () => {
            if (detailsElement.classList.contains('menu-opening')) return; // reopened meanwhile
            detailsElement.removeAttribute('open');
          },
          reducedMotion ? 0 : 280
        );
      }

      onBreakpointChange() {
        if (this.dataset.breakpoint !== 'tablet' || !this.desktopQuery.matches) return;
        if (!this.mainDetailsToggle.hasAttribute('open')) {
          this.releaseLocks();
          return;
        }
        this.closeMenuDrawer(new CustomEvent('g:breakpoint'));
      }

      onPageShow(event) {
        if (event.persisted && this.mainDetailsToggle.hasAttribute('open')) {
          this.closeMenuDrawer(event, null);
        }
      }

      setOutsideInert() {
        this.releaseInert();
        let node = this.panel;
        while (node && node !== document.body && node.parentElement) {
          const parent = node.parentElement;
          for (const sibling of parent.children) {
            if (
              sibling === node ||
              sibling.inert ||
              sibling.matches('script, style, link, template, [data-g-menu-backdrop]')
            ) {
              continue;
            }
            sibling.inert = true;
            this.inerted.push(sibling);
          }
          node = parent;
        }
      }

      releaseInert() {
        this.inerted.forEach((element) => {
          element.inert = false;
        });
        this.inerted = [];
      }

      releaseLocks() {
        this.releaseInert();
        this.desktopQuery.removeEventListener('change', this.onBreakpointChange);
        if (!this.isLocked) return;
        this.isLocked = false;
        document.body.classList.remove('g-scroll-lock', `overflow-hidden-${this.dataset.breakpoint}`);
        this.sectionHeader?.classList.remove('menu-open', 'g-menu-open');
        // Clears focus traps Dawn sets for submenus.
        if (typeof window.removeTrapFocus === 'function') window.removeTrapFocus();
      }
    }

    customElements.define('g-header-drawer', GHeaderDrawer);
  };

  if (customElements.get('menu-drawer')) {
    defineDrawer();
  } else {
    customElements.whenDefined('menu-drawer').then(defineDrawer);
  }
})();
